import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Search, CreditCard, AlertTriangle, CheckCircle2,
  Loader2, ExternalLink, FileText, Calendar, Mail, Building2, Euro,
  ShieldCheck, AlertCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import AdminPageShell from "../components/AdminPageShell";
import { API } from "../apiBase";

const fmtEur = (cents) =>
  cents == null ? "—" : `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const fmtDate = (ts) => {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

export default function AdminBilling() {
  const [subscribers, setSubscribers] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | with_vat | without_vat
  const [regularizing, setRegularizing] = useState(null); // customer_id
  const [confirmModal, setConfirmModal] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const [history, setHistory] = useState([]);

  /**
   * Loads Stripe billing state from the dedicated alias endpoints.
   *   - GET /api/admin/stripe/subscriptions  → active subscriber list
   *   - GET /api/admin/stripe/metrics        → aggregated MRR/ARR/counts
   * Both endpoints are computed live from the Stripe API (no local caching),
   * which guarantees that new subscribers, plan changes and VAT regularisations
   * are reflected as soon as the user hits "Rafraîchir".
   */
  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setErr("");
    try {
      const [subsResp, metricsResp, histResp] = await Promise.all([
        axios.get(`${API}/admin/stripe/subscriptions`),
        axios.get(`${API}/admin/stripe/metrics`),
        axios.get(`${API}/admin/billing/regularizations`).catch(() => ({ data: { items: [] } })),
      ]);
      setSubscribers(Array.isArray(subsResp.data) ? subsResp.data : []);
      setMetrics(metricsResp.data || {});
      setHistory(histResp.data?.items || []);
      if (silent) toast.success("Données Stripe actualisées");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Impossible de charger les abonnés Stripe.";
      setErr(msg);
      if (silent) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = subscribers || [];
    if (filter === "with_vat") arr = arr.filter((s) => s.has_vat);
    else if (filter === "without_vat") arr = arr.filter((s) => !s.has_vat);
    if (!query.trim()) return arr;
    const q = query.toLowerCase().trim();
    return arr.filter((s) =>
      (s.email || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.customer_id || "").toLowerCase().includes(q)
    );
  }, [subscribers, filter, query]);

  const openConfirm = async (sub) => {
    setRegularizing(sub.customer_id);
    try {
      const { data: dryRun } = await axios.post(
        `${API}/admin/billing/regularize-vat/${sub.customer_id}?dry_run=true`
      );
      setConfirmModal({ subscriber: sub, dryRun });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la simulation.");
    } finally {
      setRegularizing(null);
    }
  };

  const confirmRegularize = async () => {
    if (!confirmModal) return;
    const customerId = confirmModal.subscriber.customer_id;
    setRegularizing(customerId);
    setConfirmModal(null);
    try {
      const { data: result } = await axios.post(
        `${API}/admin/billing/regularize-vat/${customerId}`
      );
      setResultModal(result);
      await load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la régularisation.");
    } finally {
      setRegularizing(null);
    }
  };

  return (
    <AdminPageShell
      title="Facturation Stripe"
      subtitle="Abonnements actifs, statut TVA, régularisations en 1 clic — données rafraîchies en direct depuis Stripe"
      tab="billing"
      seoTitle="Admin — Facturation Stripe MetaConnect"
      seoCanonical="/admin/facturation"
    >
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={() => load({ silent: true })}
            disabled={refreshing || loading}
            data-testid="admin-billing-refresh"
            className="inline-flex items-center gap-1.5 text-[12.5px] px-4 py-2 rounded-full border border-[rgba(127,179,255,0.22)] text-[#0284C7] hover:text-[#0F2B5B] hover:border-[var(--mc-blue-400)] disabled:opacity-60 disabled:cursor-wait transition"
          >
            {refreshing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            {refreshing ? "Actualisation…" : "Rafraîchir"}
          </button>
        </div>

        {/* Stats CA */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="billing-metrics">
          <StatBig label="MRR HT" value={fmtEur(metrics.mrr_ht_cents)} sub={`${fmtEur(metrics.mrr_ttc_cents)} TTC`} icon={<Euro size={14} />} />
          <StatBig label="ARR HT" value={fmtEur(metrics.arr_ht_cents)} sub={`${fmtEur(metrics.arr_ttc_cents)} TTC`} icon={<Euro size={14} />} colorClass="border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.06)]" />
          <StatBig label="Abonnés total" value={metrics.total_subscribers || 0} icon={<CheckCircle2 size={14} />} />
          <StatBig
            label="Sans TVA (à régulariser)"
            value={metrics.subscribers_without_tax || 0}
            icon={<AlertTriangle size={14} />}
            colorClass={(metrics.subscribers_without_tax || 0) > 0 ? "border-[rgba(255,170,80,0.5)] bg-[rgba(255,170,80,0.08)]" : ""}
          />
        </div>

        {/* Filters */}
        <div className="mc-card p-4 mb-5 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Recherche par email, nom, customer_id…"
              data-testid="admin-billing-search"
              className="mc-input pl-9"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { v: "all", label: "Tous" },
              { v: "with_vat", label: "Avec TVA" },
              { v: "without_vat", label: "Sans TVA" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                data-testid={`filter-${f.v}`}
                className={`text-[11.5px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border transition ${
                  filter === f.v
                    ? "border-[#0EA5E9] bg-[#E0F2FE] text-[#0F2B5B]"
                    : "border-[rgba(127,179,255,0.22)] text-[var(--mc-muted)] hover:text-[#0F2B5B]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {err && (
          <div className="mc-card p-4 mb-4 border-[rgba(255,120,120,0.4)] bg-[rgba(255,120,120,0.05)]"
               data-testid="admin-billing-error">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <AlertTriangle size={16} className="text-[#DC2626] shrink-0" />
                <p className="text-[13px] text-[#334155]">{err}</p>
              </div>
              <button
                onClick={() => load({ silent: true })}
                disabled={refreshing}
                data-testid="admin-billing-retry"
                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-[rgba(127,179,255,0.32)] text-[#0284C7] hover:text-[#0F2B5B] hover:border-[var(--mc-blue-400)] disabled:opacity-60 transition"
              >
                {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Réessayer
              </button>
            </div>
          </div>
        )}

        {loading && <div className="mc-card p-8 text-center text-[14px] text-[var(--mc-muted)]">Chargement des abonnés Stripe…</div>}

        {!loading && filtered.length === 0 && !err && (
          <div className="mc-card p-10 text-center">
            <CreditCard size={32} className="mx-auto text-[#0284C7] mb-3" />
            <p className="text-[14px] text-[#334155]">Aucun abonné Stripe pour ce filtre.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((sub) => (
              <div key={sub.subscription_id} className="mc-card p-5" data-testid={`subscriber-${sub.customer_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-display text-[#0F2B5B] text-[16px]">{sub.name || sub.email || "Client Stripe"}</h3>
                      {sub.has_vat ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.08)] text-[var(--mc-mint)]">
                          <ShieldCheck size={10} /> TVA OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-[rgba(255,170,80,0.5)] bg-[rgba(255,170,80,0.10)] text-[#ffce99]">
                          <AlertCircle size={10} /> TVA manquante
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-[rgba(127,179,255,0.4)] bg-[rgba(127,179,255,0.08)] text-[#0284C7]">
                        {sub.interval === "year" ? "Annuel" : "Mensuel"}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px] text-[var(--mc-muted)] mt-2">
                      {sub.email && <div className="flex items-center gap-2"><Mail size={12} /><a href={`mailto:${sub.email}`} className="hover:text-[#0F2B5B]">{sub.email}</a></div>}
                      <div className="flex items-center gap-2"><Building2 size={12} /><code className="text-[11px]">{sub.customer_id}</code></div>
                      <div className="flex items-center gap-2"><Euro size={12} />
                        <span><strong className="text-[#0F2B5B]">{fmtEur(sub.amount_ht_cents)}</strong> HT / {sub.interval === "year" ? "an" : "mois"}
                          {!sub.has_vat && <span className="text-[#ffce99] ml-2">(devrait être {fmtEur(sub.amount_ttc_cents)} TTC)</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-2"><Calendar size={12} />Renouvellement : {fmtDate(sub.current_period_end)}</div>
                    </div>

                    {sub.last_invoice_url && (
                      <div className="mt-2 flex items-center gap-3 text-[11.5px]">
                        <a href={sub.last_invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1">
                          <ExternalLink size={11} /> Dernière facture
                        </a>
                        {sub.last_invoice_pdf && (
                          <a href={sub.last_invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1">
                            <FileText size={11} /> PDF
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 min-w-[180px]">
                    {!sub.has_vat ? (
                      <button
                        onClick={() => openConfirm(sub)}
                        disabled={regularizing === sub.customer_id}
                        data-testid={`regularize-${sub.customer_id}`}
                        className="btn-primary px-4 py-2.5 rounded-lg text-[12px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {regularizing === sub.customer_id ? (
                          <><Loader2 size={12} className="animate-spin" /> Patientez…</>
                        ) : (
                          <>Régulariser TVA</>
                        )}
                      </button>
                    ) : (
                      <div className="text-[11px] text-[var(--mc-mint)] text-center py-2 inline-flex items-center justify-center gap-1.5">
                        <CheckCircle2 size={12} /> Conforme
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-[#0F2B5B] text-[18px] mb-4">Historique des régularisations</h2>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="mc-card p-4 text-[12px]" data-testid={`history-${h.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CheckCircle2 size={12} className="text-[var(--mc-mint)]" />
                      <strong className="text-[#0F2B5B]">{h.customer_email}</strong>
                      <span className="text-[var(--mc-muted)]">
                        — facture <code>{h.vat_invoice_number || h.vat_invoice_id}</code>
                      </span>
                      <span className="text-[#0284C7]">{fmtEur(h.vat_cents_charged)} TVA</span>
                    </div>
                    <span className="text-[11px] text-[var(--mc-muted)]">{new Date(h.performed_at).toLocaleString("fr-FR")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Modal de confirmation */}
      {confirmModal && (
        <Modal onClose={() => setConfirmModal(null)}>
          <h3 className="font-display text-[#0F2B5B] text-[18px] mb-3">Confirmer la régularisation TVA</h3>
          <p className="text-[13px] text-[var(--mc-muted)] leading-relaxed mb-4">
            Vous êtes sur le point de prélever <strong className="text-[#0F2B5B]">{fmtEur(confirmModal.dryRun.vat_cents_to_charge)}</strong> sur le compte de <strong className="text-[#0F2B5B]">{confirmModal.dryRun.customer_email}</strong> via le mandat SEPA existant.
          </p>
          <div className="rounded-lg bg-[rgba(127,179,255,0.06)] border border-[rgba(127,179,255,0.18)] p-3 mb-4 text-[12px] text-[#334155] space-y-1">
            <div>📄 Facture initiale : <code>{confirmModal.dryRun.last_invoice_number}</code></div>
            <div>💰 Montant HT initial : {fmtEur(confirmModal.dryRun.amount_ht_cents)}</div>
            <div>📊 TVA 20% à prélever : <strong className="text-[#0F2B5B]">{fmtEur(confirmModal.dryRun.vat_cents_to_charge)}</strong></div>
            <div>🔁 Abonnements à patcher : {confirmModal.dryRun.subscriptions_to_patch.length}</div>
          </div>
          <p className="text-[11.5px] text-[var(--mc-muted)] mb-5">
            ⚠️ Cette opération est <strong>irréversible</strong>. Le client recevra automatiquement une facture conforme HT + TVA + TTC par email.
            Les futurs renouvellements de son abonnement incluront la TVA 20%.
          </p>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setConfirmModal(null)} className="px-4 py-2 rounded-lg text-[12px] text-[var(--mc-muted)] hover:text-[#0F2B5B]">Annuler</button>
            <button onClick={confirmRegularize} data-testid="confirm-regularize"
              className="btn-primary px-5 py-2 rounded-lg text-[12px] font-medium">
              Confirmer le prélèvement
            </button>
          </div>
        </Modal>
      )}

      {/* Modal de résultat */}
      {resultModal && (
        <Modal onClose={() => setResultModal(null)}>
          {resultModal.skipped ? (
            <>
              <h3 className="font-display text-[#0F2B5B] text-[18px] mb-3">Information</h3>
              <p className="text-[13px] text-[#334155]">{resultModal.reason}</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-[var(--mc-mint)]" />
                <h3 className="font-display text-[#0F2B5B] text-[18px]">Régularisation effectuée</h3>
              </div>
              <div className="text-[12.5px] text-[#334155] space-y-2 mb-4">
                <p>✅ La facture conforme a été créée et le prélèvement SEPA est en cours.</p>
                <div className="rounded-lg bg-[rgba(110,231,197,0.06)] border border-[rgba(110,231,197,0.18)] p-3 space-y-1.5">
                  <div>📧 Client : <strong>{resultModal.customer_email}</strong></div>
                  <div>🧾 Nouvelle facture : <code>{resultModal.vat_invoice_number}</code></div>
                  <div>💰 Montant prélevé : <strong>{fmtEur(resultModal.amount_charged_cents)}</strong></div>
                  <div>🔁 Abonnements patchés : {resultModal.subscriptions_patched.length}</div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {resultModal.vat_invoice_url && (
                    <a href={resultModal.vat_invoice_url} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1 border border-[rgba(127,179,255,0.3)] px-3 py-1.5 rounded-full">
                      <ExternalLink size={11} /> Voir la facture Stripe
                    </a>
                  )}
                  {resultModal.vat_invoice_pdf && (
                    <a href={resultModal.vat_invoice_pdf} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1 border border-[rgba(127,179,255,0.3)] px-3 py-1.5 rounded-full">
                      <FileText size={11} /> Télécharger PDF
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
          <div className="flex items-center justify-end">
            <button onClick={() => setResultModal(null)} className="btn-primary px-5 py-2 rounded-lg text-[12px] font-medium">Fermer</button>
          </div>
        </Modal>
      )}
    </AdminPageShell>
  );
}

function StatBig({ label, value, sub, icon, colorClass }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${colorClass || "border-[rgba(127,179,255,0.18)] bg-[rgba(127,179,255,0.03)]"}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[var(--mc-muted)]">
        {icon}{label}
      </div>
      <div className="text-[#0F2B5B] font-display text-[20px] leading-tight mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="modal-overlay">
      <div className="mc-card p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--mc-muted)] hover:text-[#0F2B5B]" aria-label="Fermer">
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}
